import { Typography, Stack, TextField, Autocomplete, ClickAwayListener, IconButton, Box, Checkbox } from "@mui/material";
import { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";
import { enqueueSnackbar } from "notistack";
import { useDispatch } from "react-redux";
import { editTicketData } from "../../../../../../redux/slices/tickets";
import UserList from "./UserList";
import Iconify from "../../../../../../reusable-components/iconify";

TicketUsers.propTypes = {
  possibleUsers: PropTypes.array,
  ticketUsers: PropTypes.array,
  setNewUsers: PropTypes.func
}
export default function TicketUsers({possibleUsers, ticketUsers, setNewUsers}) {
  const [isAddingNewUser, setIsAddingNewUser] = useState(false);
  const [localTicketUsers, setLocalTicketUsers] = useState([]);
  const dispatch = useDispatch();
  const { id = '' } = useParams();
  const subticketId = new URLSearchParams(window.location.search).get('subticket');

  const addNewUser = (newValue) => {
    const updatedUsers = newValue?.map((item) => {
      if (typeof item === 'string') {
        return { id: item, value: item };
      }
      return item;
    });
    if(setNewUsers) setNewUsers(updatedUsers);
    else {
      dispatch(editTicketData({
        Id: subticketId || id,
        value: updatedUsers.map((user) => user.id),
        updateType: 'Users',
      }))
      .then((result) => {
        if (!result) enqueueSnackbar('Failed to add user', { variant: 'error' });
        else setLocalTicketUsers(updatedUsers);
      })
      .catch(() => enqueueSnackbar('Failed to add user', { variant: 'error' }));
      setIsAddingNewUser(false);
    }
  };

  const removeUser = (userId) => {
    if(setNewUsers) setNewUsers((prev) => prev.filter(u => u.id !== userId));
    else {
      const updatedUsers = localTicketUsers?.filter((user) => user.id !== userId);
      dispatch(editTicketData({
        Id: id,
        value: updatedUsers.map((user) => user.id),
        updateType: 'Users',
      }))
      .then((result) => {
        if (!result) enqueueSnackbar('Failed to remove user', { variant: 'error' });
        else setLocalTicketUsers(updatedUsers);
      })
      .catch(() => enqueueSnackbar('Failed to remove user', { variant: 'error' }));
    }
  };

  useEffect(() => {
    setLocalTicketUsers(ticketUsers?.map(u => ({ id: u.userEmail || u.id, value: u.userName || u.value })));
  }, [ticketUsers]);


  return <>
    <Typography color="info.main" variant="h6">
      Users
    </Typography>
    <Stack sx={{ ml: 1, mt: 1, fontSize: '14px' }} direction="row" alignItems="center" flexWrap="wrap">
      <UserList users={localTicketUsers} removeUser={removeUser} />
      {isAddingNewUser ?
        <ClickAwayListener onClickAway={(e) => {
          if (
            e.target.closest('.MuiAutocomplete-popper') ||
            e.target.closest('.MuiAutocomplete-listbox')
          ) {
            return; // Ignore click away if it's on a menu item
          }
          setIsAddingNewUser(false)
        }}>
          <Autocomplete
            size="small"
            disablePortal
            multiple
            id="tags-outlined"
            options={possibleUsers || []}
            value={localTicketUsers}
            onChange={(_, newValue) => {
              const newestVal = newValue[newValue.length -1];
              if(typeof newestVal === 'string') {
                addNewUser(newValue);
              }
            }}
            filterSelectedOptions
            freeSolo
            disableClearable
            open={isAddingNewUser}
            renderTags={() => null}
            sx={{width: '300px'}}
            getOptionLabel={(option) => `${option?.value} - ${option?.id}`}
            renderOption={(props, option) => (
              <li {...props} key={option.id || option} style={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  // when ticket is new, ticketUsers will be an array of ids (email) and value(name)
                  // when the ticket already exists, ticketUsers will be an array of full user objects with userEmail, etc.
                  checked={ticketUsers.some(u => u.userEmail ?  u.userEmail === option.id : u.id === option.id)}
                  style={{ marginRight: 8 }}
                    onChange={(e) => {
                      if (!e.target.checked) {
                        removeUser(option.id);
                      }
                      else {
                        addNewUser([...possibleUsers.filter(o => ticketUsers.find(u => u.userEmail ? u.userEmail === o.id : u.id === o.id)), option]);
                      }
                    }
                  }
                />
                <Box
                  sx={{
                    maxWidth: 260,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                  }}
                >
                  {option?.value || option?.id}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Add user"
                autoComplete="off"
                onKeyDown={e => {
                  if (e.key === 'Backspace') {
                    if(params.inputProps.value === '') {
                      e.stopPropagation();
                      e.preventDefault();
                    }
                  }
                }}
              />
            )}
          /> 
        </ClickAwayListener>:
        <IconButton
          color="secondary"
          onClick={() => setIsAddingNewUser(true)}
        >
          <Iconify icon="eva:plus-fill" />
        </IconButton>}
    </Stack>
  </>
}